import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, PauseCircle, PlayCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { fetchPoczekalnia, odlozWezel } from '@/lib/agents-api';
import { useI18n } from '@/lib/i18n';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  POCZEKALNIA — na które węzły kolejka NIE czeka i ile im zalega.
 *
 *  ⚠️⚠️ POWSTAŁO Z AWARII, W KTÓREJ JEDNA MASZYNA ZATRZYMAŁA JEDENAŚCIE SPRAWNYCH (18.09.2026):
 *  „nie może być przypadku, że 11 maszyn źle raportuje, bo dwunasta się wyłączyła". Operacja
 *  config-down jest domknięta dopiero, gdy potwierdzą ją WSZYSTKIE węzły, więc jeden milczący
 *  trzymał kolejkę całej wyspy — a otwarta operacja zamraża wiersze, czyli portal przestaje
 *  przyjmować stan od maszyn, które pracują.
 *
 *  ⚠️⚠️ TEN EKRAN MA ODPOWIADAĆ NA JEDNO PYTANIE: „czy coś wisi i przez kogo". Dlatego pokazuje
 *  WSZYSTKIE węzły, a nie tylko odłożone — lista, na której nie ma nikogo, nie odróżnia „wszystko
 *  gra" od „nie wiem, czy to w ogóle działa".
 *
 *  ⚠️ ODŁOŻENIE TO NIE ODRZUCENIE i tak ma brzmieć na ekranie. Węzeł zachowuje prawo zapisu
 *  i wszystkie zaległości; zmienia się wyłącznie to, czy reszta floty ma na niego czekać.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function Poczekalnia({ groupId }: { groupId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();

  const dane = useQuery({
    queryKey: ['poczekalnia', groupId],
    queryFn: () => fetchPoczekalnia(groupId),
    /** ⚠️ Stan liczy się z czasu ciszy, więc odświeża się sam — bez tego ekran kłamałby po minucie. */
    refetchInterval: 30_000,
  });

  const przelacz = useMutation({
    mutationFn: (v: { id: string; odloz: boolean }) => odlozWezel(v.id, v.odloz),
    onSuccess: async (w) => {
      toast.success(w.odlozony ? t('poczekalnia.odlozony') : t('poczekalnia.przywrocony'));
      await qc.invalidateQueries({ queryKey: ['poczekalnia', groupId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const wezly = dane.data?.wezly ?? [];
  const odlozonych = wezly.filter((w) => w.wPoczekalni).length;

  return (
    <section className="space-y-2 rounded-lg border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-medium">{t('poczekalnia.tytul')}</h2>
        {odlozonych > 0 && (
          <Badge variant="warning">
            {t('poczekalnia.licznik').replace('{ile}', String(odlozonych))}
          </Badge>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {t('poczekalnia.poCo').replace('{prog}', String(dane.data?.progMinut ?? 60))}
      </p>

      {dane.isPending ? (
        <Loader2 className="size-4 animate-spin text-muted-foreground" />
      ) : wezly.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{t('poczekalnia.brakWezlow')}</p>
      ) : (
        <ul className="space-y-1">
          {wezly.map((w) => (
            <li
              key={w.id}
              className="flex flex-wrap items-center gap-2 rounded border border-border px-2 py-1.5 text-xs"
            >
              <span className="font-mono font-medium">{w.nodeId}</span>
              {w.hostname !== null && <span className="text-muted-foreground">{w.hostname}</span>}

              {w.wPoczekalni ? (
                <Badge variant="warning">{t(`poczekalnia.powod.${w.powod ?? 'CISZA'}`)}</Badge>
              ) : (
                <Badge variant="neutral">{t('poczekalnia.czeka')}</Badge>
              )}

              {/*
                ⚠️ MINUTY CISZY PRZY KAŻDYM WĘŹLE, nie tylko przy odłożonym: to jedyna liczba,
                po której widać, że ktoś ZARAZ tam trafi — a wtedy jest jeszcze czas zareagować.
              */}
              <span className="text-muted-foreground">
                {w.minutOdKontaktu === null
                  ? t('poczekalnia.nigdy')
                  : t('poczekalnia.cisza').replace('{min}', String(w.minutOdKontaktu))}
              </span>

              {/* ⚠️ Zaległość liczona TYLKO dla odłożonych — patrz serwis; `null` znaczy „nie pytaliśmy". */}
              {w.zalegaOperacji !== null && w.zalegaOperacji > 0 && (
                <Badge variant="neutral">
                  {t('poczekalnia.zalega').replace('{ile}', String(w.zalegaOperacji))}
                </Badge>
              )}

              <Button
                size="sm"
                variant="outline"
                className="ml-auto"
                disabled={przelacz.isPending}
                onClick={() => przelacz.mutate({ id: w.id, odloz: w.odlozonyOd === null })}
              >
                {w.odlozonyOd === null ? (
                  <>
                    <PauseCircle className="size-3.5" /> {t('poczekalnia.odloz')}
                  </>
                ) : (
                  <>
                    <PlayCircle className="size-3.5" /> {t('poczekalnia.przywroc')}
                  </>
                )}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/*
        ⚠️ ZDANIE O ZALEGŁOŚCIACH MUSI STAĆ NA EKRANIE. „Odłożony" brzmi jak „pominięty na zawsze",
        a to jest dokładnie to, czego ten mechanizm NIE robi — i pierwsze, o co ktoś zapyta.
      */}
      <p className="text-[11px] text-muted-foreground">{t('poczekalnia.zaleglosciZostaja')}</p>
    </section>
  );
}
